import {Badge, Card, createStyles, Group, SimpleGrid, Stack, Text} from '@mantine/core';
import React from 'react';

import {
  AnalysisTemplateConfigVariableGraphQL,
  AnalysisTemplateDataFrameVariableGraphQL,
  AnalysisTemplateGraphQL,
} from 'analysis-tool/templates/AnalysisTemplateTypes';
import {
  getFileConfigVariables,
  getIconForConfigVariable,
  showConfigVariableAsRequired,
} from 'analysis-tool/templates/AnalysisTemplateVariableUtils';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import HelpButton from 'help/HelpButton';
import useStepTypeInfos, {StepTypeInfo} from 'pipelines/hooks/useStepTypeInfosQuery';
import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {FieldRequiredIndicator} from 'ui-library/inputs/FieldLabel';

const SOURCE_TABLES_DESCRIPTION = [
  'Source tables are inputs for which the user provides a data table.',
  'To add a source table to a template, add a "Create table from dataset" step and select "Variable".',
].join('\n\n');

const SOURCE_FILES_DESCRIPTION = [
  'Source files are inputs for which the user provides a file, such as a CSV or plate reader output file.',
  'To add a source file to a template, add an "Import files from data catalog" step.',
].join('\n\n');

const TEMPLATE_VARIABLES_DESCRIPTION = [
  'Template variables are inputs for which the user provides a single value or a list of values, such as a text value, a folder selection, or a list of entities.',
  'Template variables become fields the user fills out in a form.',
  'Some template variables are created automatically for steps such as "Export table as dataset" or "Send outputs".',
  'To add your own template variables, add a "Create table from template variables" step.',
].join('\n\n');

interface InputCardProps {
  description?: string;
  icon: BenchlingIconName | null;
  isMulti: boolean;
  isRequired: boolean;
  name: string;
}

/**
 * A card describing a single input (table, file, or variable) that a user must supply when applying an
 * analysis template.
 */
function InputCard({icon, name, isMulti, isRequired, description}: InputCardProps): JSX.Element {
  return (
    <Card withBorder={true} padding="sm" radius="sm">
      <Stack spacing="xs">
        <Group spacing="sm" noWrap={true} position="apart">
          <Group spacing="sm" noWrap={true}>
            {icon != null && <BenchlingIcon icon={icon} />}
            <Group spacing="s" noWrap={true}>
              <Text>{name}</Text>
              {isRequired && <FieldRequiredIndicator />}
            </Group>
          </Group>
          {isMulti && (
            <Badge color="gray" size="xs">
              Multi-select
            </Badge>
          )}
        </Group>
        {description != null && <Text>{description}</Text>}
      </Stack>
    </Card>
  );
}

const useStyles = createStyles(() => ({
  helpText: {
    whiteSpace: 'pre-wrap',
  },
}));

function InputsSubsection<T>({
  title,
  description,
  emptyMessage,
  items,
  getKey,
  getCardProps,
}: {
  description: string;
  emptyMessage: string;
  getCardProps: (item: T) => InputCardProps;
  getKey: (item: T) => string;
  items: ReadonlyArray<T>;
  title: string;
}): JSX.Element {
  const {classes} = useStyles();

  return (
    <Stack spacing="xs">
      <Group spacing="xs" noWrap={true} align="center">
        <Text size="sm" weight={600}>
          {title}
        </Text>
        <HelpButton renderContent={() => <Text className={classes.helpText}>{description}</Text>} />
      </Group>
      {items.length > 0 ? (
        <SimpleGrid cols={2} spacing="md">
          {items.map((item) => (
            <InputCard key={getKey(item)} {...getCardProps(item)} />
          ))}
        </SimpleGrid>
      ) : (
        <Text size="sm" color="dimmed">
          {emptyMessage}
        </Text>
      )}
    </Stack>
  );
}

function getDataFrameVariableCardProps(variable: AnalysisTemplateDataFrameVariableGraphQL): InputCardProps {
  return {
    icon: 'table',
    name: variable.name,
    isMulti: false,
    // Input tables are always required.
    isRequired: true,
    description: variable.description || undefined,
  };
}

/**
 * Look up the step type of the step that a config variable belongs to, from the template's `steps`.
 */
function getConfigVariableStepType(
  analysisTemplate: AnalysisTemplateGraphQL,
  variable: AnalysisTemplateConfigVariableGraphQL
): PipelineStepType | undefined {
  return analysisTemplate.steps.find((step) => step.id === variable.pipelineStepDomain.id)?.stepType;
}

/**
 * The position of the step that a config variable belongs to, within the template's `steps`. Variables
 * whose step can't be found sort last.
 */
function getConfigVariableStepIndex(
  analysisTemplate: AnalysisTemplateGraphQL,
  variable: AnalysisTemplateConfigVariableGraphQL
): number {
  const index = analysisTemplate.steps.findIndex((step) => step.id === variable.pipelineStepDomain.id);
  return index === -1 ? Infinity : index;
}

/**
 * Sort config variables primarily by the order of their step within the template's `steps`, and
 * secondarily by name.
 */
function sortConfigVariables<T extends AnalysisTemplateConfigVariableGraphQL>(
  analysisTemplate: AnalysisTemplateGraphQL,
  configVariables: ReadonlyArray<T>
): Array<T> {
  return [...configVariables].sort((a, b) => {
    const stepIndexDifference =
      getConfigVariableStepIndex(analysisTemplate, a) - getConfigVariableStepIndex(analysisTemplate, b);
    return stepIndexDifference !== 0 ? stepIndexDifference : a.name.localeCompare(b.name);
  });
}

/**
 * Build a `getCardProps` callback that describes a config variable with the user-facing name of the step
 * type it comes from (e.g. "Upload file"), so users understand where a variable is configured.
 */
function getConfigVariableCardProps(
  analysisTemplate: AnalysisTemplateGraphQL,
  stepTypeInfos: Map<PipelineStepType, StepTypeInfo>
): (variable: AnalysisTemplateConfigVariableGraphQL) => InputCardProps {
  return (variable) => {
    const stepType = getConfigVariableStepType(analysisTemplate, variable);
    const stepTypeUserFacingName = stepType != null ? stepTypeInfos.get(stepType)?.userFacingName : undefined;
    return {
      icon: getIconForConfigVariable(variable),
      name: variable.name,
      isMulti: variable.variableTypeConfigSpec.kind === 'array',
      isRequired: showConfigVariableAsRequired(variable),
      description: stepTypeUserFacingName,
    };
  };
}

export interface VariablesSummaryTabProps {
  analysisTemplate: AnalysisTemplateGraphQL;
}

/**
 * Summarizes the inputs (tables, files, and variables) that a user must supply when applying an analysis
 * template.
 *
 * @package
 */
export default function VariablesSummaryTab({analysisTemplate}: VariablesSummaryTabProps): JSX.Element {
  const stepTypeInfos = useStepTypeInfos();
  const sortedConfigVariables = sortConfigVariables(analysisTemplate, analysisTemplate.configVariables);
  const fileConfigVariables = getFileConfigVariables(sortedConfigVariables);
  const otherConfigVariables = sortedConfigVariables.filter(
    (configVariable) => !fileConfigVariables.includes(configVariable)
  );

  return (
    <Stack spacing="md" {...getTestProps({component: 'VariablesSummaryTab', element: 'root'})}>
      <InputsSubsection<AnalysisTemplateDataFrameVariableGraphQL>
        title="Source tables"
        description={SOURCE_TABLES_DESCRIPTION}
        emptyMessage="This template has no source tables"
        items={analysisTemplate.dataFrameVariables}
        getKey={(variable) => variable.id}
        getCardProps={getDataFrameVariableCardProps}
      />
      <InputsSubsection<AnalysisTemplateConfigVariableGraphQL>
        title="Source files"
        description={SOURCE_FILES_DESCRIPTION}
        emptyMessage="This template has no source files"
        items={fileConfigVariables}
        getKey={(variable) => variable.id}
        getCardProps={getConfigVariableCardProps(analysisTemplate, stepTypeInfos)}
      />
      <InputsSubsection<AnalysisTemplateConfigVariableGraphQL>
        title="Template variables"
        description={TEMPLATE_VARIABLES_DESCRIPTION}
        emptyMessage="This template has no template variables"
        items={otherConfigVariables}
        getKey={(variable) => variable.id}
        getCardProps={getConfigVariableCardProps(analysisTemplate, stepTypeInfos)}
      />
    </Stack>
  );
}
